// src/pages/ProfileSettingsPage.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import api, { updateUserAvatar, resolveUploadUrl } from '../api';
import { useAuth } from '../context/AuthContext';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import {
  Mail,
  Save,
  Key,
  Server,
  Shield,
  Globe,
  Twitter,
  Instagram,
  Github,
  UserCircle,
  Link,
  Terminal,
  Check,
  AlertCircle,
  Crown,
  Loader2,
  Trash2,
  Camera,
} from 'lucide-react';

// ============================================================
// ✅ SECURITY HELPERS
// ============================================================

/**
 * 1. SANITIZE TEXT
 * Strips HTML tags and dangerous characters from text inputs.
 * Prevents Stored XSS attacks.
 */
const sanitizeText = (input) => {
  if (!input) return "";
  return String(input)
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
};

/**
 * 2. VALIDATE & SANITIZE URL
 * Ensures social links use safe protocols (http/https).
 * Prevents javascript: protocol injection.
 */
const sanitizeUrl = (url) => {
  if (!url) return "";
  
  // Trim whitespace
  const trimmed = url.trim();
  
  // If empty after trim
  if (!trimmed) return "";

  // Check for safe protocol
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  
  // If user typed "twitter.com/user" (no protocol), auto-fix to https
  // This is user-friendly and ensures a safe protocol is attached
  if (/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/.test(trimmed)) {
      return `https://${trimmed}`;
  }

  // If it looks like a dangerous protocol (javascript:, data:), return empty or keep original
  // We will return empty to break the link, as it's invalid.
  if (/:/.test(trimmed)) {
      return ""; 
  }
  
  // Default fallback (could be a relative path, though unlikely for social)
  return trimmed;
};

export default function ProfileSettingsPage() {
  const navigate = useNavigate();
  const { currentUser: contextUser } = useOutletContext(); 
  const { refreshUser } = useAuth();

  const [freshUser, setFreshUser] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(true);
  
  const [allSettings, setAllSettings] = useState({
    name: '',
    username: '',
    bio: '',
    avatar: '',
    email: '',
    twitter: '',
    instagram: '',
    github: '',
    website: '',
    accountId: '',
    password: '',
    server: '',
    isDemo: true,
  });

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // --- 1. FETCH FRESH DATA ON MOUNT ---
  const fetchInitialData = useCallback(async () => {
    setIsLoadingData(true);
    try {
      const userRes = await api.get('/users/me');
      const userData = userRes.data;
      setFreshUser(userData);

      let mt5Creds = { login: '', server: '', is_demo: true };
      try {
        const mt5Res = await api.get('/mt5/credentials');
        if (mt5Res.data) {
          mt5Creds = mt5Res.data;
        }
      } catch (e) {
        console.warn("MT5 credentials endpoint failed", e);
      }

      setAllSettings({
        name: userData.name || '',
        username: userData.username || '',
        bio: userData.bio || '',
        avatar: userData.avatar || '',
        email: userData.email || '',
        twitter: userData.twitter || '',
        instagram: userData.instagram || '',
        github: userData.github || '',
        website: userData.website || '',
        accountId: mt5Creds.login || '',
        password: '',
        server: mt5Creds.server || '',
        isDemo: mt5Creds.is_demo ?? true,
      });

    } catch (err) {
      console.error("Failed to load profile data", err);
      setErrorMessage("Could not load your settings.");
    } finally {
      setIsLoadingData(false);
    }
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Silent pull-to-refresh (mobile)
  const containerRef = usePullToRefresh(fetchInitialData);

  // --- 2. DERIVE PREMIUM STATUS ---
  const activeUser = freshUser || contextUser;
  const isPremium = activeUser?.is_premium || false;

  // --- HANDLERS ---
  
  const handleInputChange = (field, value) => {
    setAllSettings(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
    setSuccessMessage('');
    setErrorMessage('');
  };

  const applyAvatar = async (avatarUrl) => {
    setAllSettings(prev => ({ ...prev, avatar: avatarUrl }));
    setFreshUser(prev => (prev ? { ...prev, avatar: avatarUrl } : prev));
    // Push the change into AuthContext so the photo shows everywhere immediately.
    if (refreshUser) await refreshUser();
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow picking the same file again later
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setErrorMessage('Unsupported image type. Use JPG, PNG, WEBP or GIF.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrorMessage('Image is larger than 5 MB.');
      return;
    }

    setIsUploadingAvatar(true);
    setSuccessMessage('');
    setErrorMessage('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await updateUserAvatar(formData);
      await applyAvatar(res.data?.avatar || '');
      setSuccessMessage('Profile photo updated.');
    } catch (err) {
      console.error('Avatar upload failed', err);
      setErrorMessage(err.response?.data?.detail || 'Photo upload failed. Try again.');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleRemoveAvatar = async () => {
    if (!window.confirm('Remove your profile photo?')) return;
    setIsUploadingAvatar(true);
    setSuccessMessage('');
    setErrorMessage('');
    try {
      await api.put('/user/profile', { avatar: '' });
      await applyAvatar('');
      setSuccessMessage('Profile photo removed.');
    } catch (err) {
      console.error('Avatar removal failed', err);
      setErrorMessage(err.response?.data?.detail || 'Could not remove the photo.');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleDeleteMT5 = async () => {    if (!window.confirm("Are you sure you want to delete your MT5 credentials? You will need to reconnect to continue syncing.")) {
      return;
    }

    setIsDeleting(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      await api.delete('/mt5/credentials');
      
      setAllSettings(prev => ({ ...prev, accountId: '', password: '', server: '' }));
      setHasChanges(false);
      
      const userRefresh = await api.get('/users/me');
      setFreshUser(userRefresh.data);
      
      setSuccessMessage('MT5 account deleted successfully.');
    } catch (err) {
      console.error("Delete failed", err);
      setErrorMessage(err.response?.data?.detail || "Failed to delete MT5 account.");
    } finally {
      setIsDeleting(false);
    }
  };

  // --- 3. SAVE ALL SETTINGS (SECURED) ---
  const handleSaveAll = async () => {
    setIsSaving(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      // ✅ SECURITY: Sanitize Profile Data
      await api.put('/user/profile', {
        name: sanitizeText(allSettings.name),
        username: allSettings.username || null,
        bio: sanitizeText(allSettings.bio),
        // Avatar URL should be sanitized as a URL
        avatar: sanitizeUrl(allSettings.avatar),
      });

      // ✅ SECURITY: Sanitize Social URLs
      await api.put('/user/social', {
        twitter: sanitizeUrl(allSettings.twitter),
        instagram: sanitizeUrl(allSettings.instagram),
        github: sanitizeUrl(allSettings.github),
        website: sanitizeUrl(allSettings.website),
      });

      if (isPremium && allSettings.accountId) {
        const mt5Payload = {
          // Login ID is typically numeric/string but safe to trim
          login: String(allSettings.accountId).trim(),
          server: sanitizeText(allSettings.server), // Server name is text
          is_demo: allSettings.isDemo,
        };

        // ⚠️ SECURITY: DO NOT SANITIZE PASSWORD
        // Sanitizing would change the user's actual password characters (e.g., if they use '<')
        if (allSettings.password && allSettings.password.trim() !== '') {
          mt5Payload.password = allSettings.password;
        }

        await api.post('/mt5/credentials', mt5Payload);
        
        const userRefresh = await api.get('/users/me');
        setFreshUser(userRefresh.data);
      }

      setSuccessMessage('Settings saved successfully!');
      setHasChanges(false);
      setAllSettings(prev => ({ ...prev, password: '' }));
      if (refreshUser) await refreshUser();

    } catch (err) {
      console.error("Save failed", err);
      setErrorMessage(err.response?.data?.detail || "Failed to save settings.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoadingData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-gray-900">
        <Loader2 className="animate-spin text-blue-500" size={32} />
      </div>
    );
  }

  return (
    <div className="h-full bg-slate-50 dark:bg-gray-900 p-4 md:p-6 max-w-4xl mx-auto pb-24 transition-colors duration-300 overflow-y-auto overflow-x-hidden">
      
      {/* Sticky compact bar — hero block scrolls beneath it */}
      <header className="sticky top-0 z-10 -mx-4 md:-mx-6 px-4 md:px-6 pt-12 md:pt-3 pb-3 bg-slate-50/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-800">
        <h1 className="text-lg font-bold text-gray-900 dark:text-white">Profile Settings</h1>
      </header>

      <div className="pt-8 pb-6 mb-4 flex flex-col items-center">
          <div className="relative mb-4">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-600 rounded-3xl blur-2xl opacity-20 dark:opacity-40 transition-opacity"></div>
            <div className="relative inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 via-purple-500 to-purple-600 rounded-2xl shadow-xl">
              <UserCircle size={32} className="text-white drop-shadow-lg" />
            </div>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Profile Settings</h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Manage your account details</p>
      </div>

      {/* Messages */}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/30 rounded-xl text-green-600 dark:text-green-400 text-sm flex items-center">
          <Check size={16} className="mr-2 flex-shrink-0" />
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-xl text-red-600 dark:text-red-400 text-sm flex items-center">
          <AlertCircle size={16} className="mr-2 flex-shrink-0" />
          {errorMessage}
        </div>
      )}

      <div className="max-w-4xl mx-auto space-y-6">

        
        {/* Profile Section */}
        <div className="bg-white dark:bg-gray-800/30 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-gray-700/50 p-6 shadow-sm dark:shadow-none">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <UserCircle className="mr-2 text-blue-500 dark:text-blue-400" size={20} />
            Profile Information
          </h2>
          
          <div className="space-y-4">
            {/* Profile photo */}
            <div className="flex items-center gap-4">
              <div className="relative shrink-0">
                {allSettings.avatar ? (
                  <img
                    src={resolveUploadUrl(allSettings.avatar)}
                    alt="Avatar"
                    className="w-20 h-20 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold">
                    {(allSettings.name || 'U').charAt(0).toUpperCase()}
                  </div>
                )}
                {isUploadingAvatar && (
                  <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                    <Loader2 size={20} className="animate-spin text-white" />
                  </div>
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">Profile photo</p>
                <p className="text-xs text-gray-400 mb-2">JPG, PNG, WEBP or GIF · up to 5 MB. Shown across the app.</p>
                <div className="flex items-center gap-2">
                  <label
                    className={`inline-flex items-center text-xs font-medium text-white bg-blue-500 hover:bg-blue-600 px-3 py-1.5 rounded-xl cursor-pointer transition-colors ${isUploadingAvatar ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <Camera size={14} className="mr-1.5" />
                    {allSettings.avatar ? 'Change photo' : 'Upload photo'}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      onChange={handleAvatarUpload}
                      disabled={isUploadingAvatar}
                    />
                  </label>
                  {allSettings.avatar && (
                    <button
                      type="button"
                      onClick={handleRemoveAvatar}
                      disabled={isUploadingAvatar}
                      className="text-xs text-red-500 hover:text-red-400 disabled:opacity-50 transition-colors"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Name</label>
              <input
                type="text"
                value={allSettings.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full bg-gray-100 dark:bg-gray-700/50 text-gray-900 dark:text-white px-4 py-2.5 rounded-xl border border-gray-200 dark:border-transparent focus:border-blue-500 focus:outline-none transition-all"
                placeholder="Enter your name"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Username</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 text-sm">@</span>
                <input
                  type="text"
                  value={allSettings.username}
                  onChange={(e) => handleInputChange('username', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  className="w-full bg-gray-100 dark:bg-gray-700/50 text-gray-900 dark:text-white pl-8 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-transparent focus:border-blue-500 focus:outline-none transition-all"
                  placeholder="username"
                  maxLength={20}
                />
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">3-20 characters: letters, numbers, underscores only</p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={16} />
                <input
                  type="email"
                  value={allSettings.email}
                  disabled
                  className="w-full bg-gray-100 dark:bg-gray-700/30 text-gray-500 dark:text-gray-400 pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-transparent cursor-not-allowed"
                  placeholder="Email cannot be changed"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Bio</label>
              <textarea
                value={allSettings.bio}
                onChange={(e) => handleInputChange('bio', e.target.value)}
                className="w-full bg-gray-100 dark:bg-gray-700/50 text-gray-900 dark:text-white px-4 py-2.5 rounded-xl border border-gray-200 dark:border-transparent focus:border-blue-500 focus:outline-none transition-all resize-none"
                rows={2}
                placeholder="Tell us about yourself"
              />
            </div>
          </div>
        </div>

        {/* MT5 Integration Section */}
        <div className="relative bg-white dark:bg-gray-800/30 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-gray-700/50 p-6 overflow-hidden shadow-sm dark:shadow-none">
          
          {!isPremium && (
            <div className="absolute inset-0 bg-white/90 dark:bg-gray-900/80 backdrop-blur-sm z-10 flex flex-col items-center justify-center p-6 text-center rounded-2xl">
              <div className="bg-gradient-to-br from-purple-600 to-blue-600 p-3 rounded-full mb-3 shadow-lg">
                <Crown size={24} className="text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">Premium Feature</h3>
              <p className="text-gray-500 dark:text-gray-400 text-xs mb-4 max-w-[220px]">
                Subscribe to Premium to connect your MT5 account and unlock patterns.
              </p>
              <button
                onClick={() => navigate('/dashboard/subscription')}
                className="bg-gradient-to-r from-yellow-500 to-orange-500 text-black text-sm font-bold py-2 px-6 rounded-xl hover:opacity-90 transition-opacity"
              >
                Upgrade Now
              </button>
            </div>
          )}

          <div className="flex justify-between items-center mb-4">
             <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <Terminal className="mr-2 text-purple-500 dark:text-purple-400" size={20} />
              MT5 Integration
            </h2>
            
            {isPremium && allSettings.accountId && (
              <button
                onClick={handleDeleteMT5}
                disabled={isDeleting}
                className="flex items-center text-xs text-red-500 hover:text-red-400 dark:text-red-400 dark:hover:text-red-300 transition-colors disabled:opacity-50"
              >
                {isDeleting ? (
                  <Loader2 size={14} className="animate-spin mr-1" />
                ) : (
                  <Trash2 size={14} className="mr-1" />
                )}
                Delete Account
              </button>
            )}
          </div>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Account ID</label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={16} />
                  <input
                    type="text"
                    value={allSettings.accountId}
                    onChange={(e) => handleInputChange('accountId', e.target.value)}
                    disabled={!isPremium}
                    autoComplete="off"
                    className="w-full bg-gray-100 dark:bg-gray-700/50 text-gray-900 dark:text-white pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-transparent focus:border-blue-500 focus:outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="MT5 Login ID"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Server</label>
                <div className="relative">
                  <Server className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={16} />
                  <input
                    type="text"
                    value={allSettings.server}
                    onChange={(e) => handleInputChange('server', e.target.value)}
                    disabled={!isPremium}
                    autoComplete="off"
                    className="w-full bg-gray-100 dark:bg-gray-700/50 text-gray-900 dark:text-white pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-transparent focus:border-blue-500 focus:outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="e.g., ICMarkets-Demo"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Password</label>
              <div className="relative">
                <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={16} />
                <input
                  type="password"
                  value={allSettings.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  disabled={!isPremium}
                  autoComplete="new-password"
                  className="w-full bg-gray-100 dark:bg-gray-700/50 text-gray-900 dark:text-white pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-transparent focus:border-blue-500 focus:outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Enter MT5 Password to update"
                />
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Leave blank to keep existing password.</p>
            </div>

            <div className="flex items-center gap-4 pt-2">
              <button
                type="button"
                onClick={() => handleInputChange('isDemo', true)}
                disabled={!isPremium}
                className={`flex items-center text-sm transition-colors ${
                  allSettings.isDemo ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'
                } disabled:opacity-50`}
              >
                <Globe className="mr-1.5" size={14} />
                Demo
                {allSettings.isDemo && <Check size={14} className="ml-1" />}
              </button>
              
              <button
                type="button"
                onClick={() => handleInputChange('isDemo', false)}
                disabled={!isPremium}
                className={`flex items-center text-sm transition-colors ${
                  !allSettings.isDemo ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'
                } disabled:opacity-50`}
              >
                <Globe className="mr-1.5" size={14} />
                Live
                {!allSettings.isDemo && <Check size={14} className="ml-1" />}
              </button>
            </div>
          </div>
        </div>

        {/* Social Links Section */}
        <div className="bg-white dark:bg-gray-800/30 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-gray-700/50 p-6 shadow-sm dark:shadow-none">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <Link className="mr-2 text-green-500 dark:text-green-400" size={20} />
            Social Links
          </h2>
          
          <div className="space-y-4">
            <div className="relative">
              <Twitter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={16} />
              <input
                type="url"
                value={allSettings.twitter}
                onChange={(e) => handleInputChange('twitter', e.target.value)}
                className="w-full bg-gray-100 dark:bg-gray-700/50 text-gray-900 dark:text-white pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-transparent focus:border-blue-500 focus:outline-none transition-all"
                placeholder="Twitter URL"
              />
            </div>

            <div className="relative">
              <Instagram className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={16} />
              <input
                type="url"
                value={allSettings.instagram}
                onChange={(e) => handleInputChange('instagram', e.target.value)}
                className="w-full bg-gray-100 dark:bg-gray-700/50 text-gray-900 dark:text-white pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-transparent focus:border-blue-500 focus:outline-none transition-all"
                placeholder="Instagram URL"
              />
            </div>

            <div className="relative">
              <Github className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={16} />
              <input
                type="url"
                value={allSettings.github}
                onChange={(e) => handleInputChange('github', e.target.value)}
                className="w-full bg-gray-100 dark:bg-gray-700/50 text-gray-900 dark:text-white pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-transparent focus:border-blue-500 focus:outline-none transition-all"
                placeholder="GitHub URL"
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        {hasChanges && (
          <div className="mt-4 flex justify-center">
            <button
              onClick={handleSaveAll}
              disabled={isSaving}
              className="w-full md:w-auto px-8 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-md"
            >
              {isSaving ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Save Changes
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Global Styles for Autofill */}
      <style>{`
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
            -webkit-box-shadow: 0 0 0 30px #f3f4f6 inset !important;
            -webkit-text-fill-color: #111827 !important;
        }
        
        .dark input:-webkit-autofill,
        .dark input:-webkit-autofill:hover,
        .dark input:-webkit-autofill:focus,
        .dark input:-webkit-autofill:active {
            -webkit-box-shadow: 0 0 0 30px #374151 inset !important;
            -webkit-text-fill-color: #ffffff !important;
        }
      `}</style>
    </div>
  );
}